import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Download, Users, BarChart3, Target, Calendar, Brain,
  TrendingUp, Shield, Clock, Award, CheckCircle, ChevronRight, Printer,
  FileText, Dumbbell, Apple, Moon, Heart, LineChart, UserPlus, Mail,
  CreditCard, Share2, Clipboard, GitCompareArrows, FolderOpen, Activity
} from 'lucide-react';

const CoachBenefitsPage = () => {
  const location = useLocation();
  const isFromDashboard = location.pathname.includes('/coach/');

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#2C5530] text-white py-6 print:hidden">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link to={isFromDashboard ? "/coach" : "/coach-login"} className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {isFromDashboard ? 'Back to Dashboard' : 'Back to Coach Login'}
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors rounded" data-testid="print-btn">
              <Printer className="w-4 h-4" /> Print
            </button>
            <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-white/90 text-[#2C5530] font-semibold transition-colors rounded" data-testid="download-btn">
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 print:py-0 print:px-0 print:max-w-none">
        
        {/* Title */}
        <div className="text-center mb-16 print:mb-8">
          <div className="flex justify-center mb-6">
            <img src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" alt="Quiet Mind Golf" className="h-20 w-auto" />
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1A1A1A] mb-4 print:text-3xl">Coach Portal Benefits</h1>
          <p className="text-xl text-[#57534E] max-w-2xl mx-auto print:text-base">
            A comprehensive platform designed to elevate your coaching practice, improve golfer retention, and deliver measurable results.
          </p>
        </div>

        {/* Executive Summary */}
        <section className="bg-white border border-[#E7E5E4] p-8 mb-12 print:border-0 print:shadow-none print:mb-6">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-4 flex items-center gap-3">
            <FileText className="w-6 h-6 text-[#2C5530]" /> Executive Summary
          </h2>
          <p className="text-[#57534E] leading-relaxed mb-4">
            The Quiet Mind Golf Coach Portal is a powerful digital platform that transforms how golf coaches 
            track, analyse, and improve their students' mental and physical performance. With comprehensive reporting, 
            student grouping, and cross-player comparison tools, coaches can deliver 
            personalised, data-driven coaching that produces measurable results.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">5</p>
              <p className="text-sm text-[#57534E]">Detailed Reports</p>
            </div>
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">4</p>
              <p className="text-sm text-[#57534E]">Performance Pillars</p>
            </div>
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">Groups</p>
              <p className="text-sm text-[#57534E]">Team Management</p>
            </div>
            <div className="text-center p-4 bg-[#F2F0E9] rounded">
              <p className="text-3xl font-bold text-[#2C5530]">25%</p>
              <p className="text-sm text-[#57534E]">Bulk Discount</p>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="mb-12 print:mb-6">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Award className="w-6 h-6 text-[#2C5530]" /> Key Benefits for Coaches
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Users, title: 'Centralised Student Management', desc: 'View all students in one dashboard. Add, edit, group, and track activity at a glance. Quickly identify who needs attention and who\'s thriving.' },
              { icon: BarChart3, title: '5 Comprehensive Reports', desc: 'Access Performance Summary, Practice Sessions, Game Stats, Scorecards, and Pillars of Performance reports — all with interactive charts and date range filtering.' },
              { icon: GitCompareArrows, title: 'Cross-Student Comparison', desc: 'Compare any combination of students or entire groups across scoring, sessions, pillars, and scorecards with radar and bar charts.' },
              { icon: FolderOpen, title: 'Student Groups', desc: 'Organise students into custom groups — High School Teams, College Squads, Beginner Ladies, etc. Select entire groups for comparison reports.' },
              { icon: TrendingUp, title: 'Track Improvement Over Time', desc: 'Monitor trends in mental scores, mood patterns, and performance metrics. Demonstrate tangible progress with data-backed charts.' },
              { icon: Activity, title: 'Enhanced Session Logging', desc: 'Students log Mental, Technical, and On-Course coaching sessions with specific focus areas, goals, and mood tracking — all visible to you.' },
              { icon: Clock, title: 'Save Time on Admin', desc: 'Automated weekly digest emails summarise all student activity. Spend less time chasing updates and more time coaching.' },
            ].map((b, i) => (
              <div key={i} className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2C5530] text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] text-lg mb-2">{b.title}</h3>
                    <p className="text-[#57534E] text-sm">{b.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reporting Suite */}
        <section className="mb-12 print:mb-6 print:break-before-page">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-[#2C5530]" /> Reporting Suite
          </h2>
          <p className="text-[#57534E] mb-6">
            View individual reports or select "All Reports" for a comprehensive overview. Every report includes 
            date range filtering, interactive charts, and the ability to print or download.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: TrendingUp, title: 'Performance Summary', items: ['Score & mental score trend lines', 'Mood before vs after analysis', 'Total sessions, rounds & scorecards', 'Overall averages & progression'] },
              { icon: Activity, title: 'Practice Sessions', items: ['Session type breakdown (pie chart)', 'Focus areas frequency (bar chart)', 'Duration trends over time', 'Full session log with mood data'] },
              { icon: Target, title: 'Scoring & Game Stats', items: ['Score progression chart', 'Putts, FIR, GIR tracking', 'Best/worst round analysis', 'Mental vs actual score comparison'] },
              { icon: Brain, title: 'Performance Scorecards', items: ['Before/During/In-Between/After radar charts', 'Category average scores', 'Scorecard score trend over time', 'Strengths & areas for improvement'] },
              { icon: Dumbbell, title: 'Pillars of Performance', items: ['Activity breakdown (pie chart)', 'Weekly stacked bar chart', 'Meditation, Movement, Nutrition, Recovery', 'Total entries & minutes tracked'] },
            ].map((r, i) => (
              <div key={i} className="bg-white border border-[#E7E5E4] p-5 print:border-gray-300">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-[#2C5530]/10 text-[#2C5530] flex items-center justify-center rounded">
                    <r.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A]">{r.title}</h3>
                </div>
                <ul className="text-sm text-[#57534E] space-y-1">
                  {r.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Groups & Comparison */}
        <section className="mb-12 print:mb-6 print:break-before-page">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <GitCompareArrows className="w-6 h-6 text-[#2C5530]" /> Groups & Cross-Student Comparison
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
              <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[#2C5530]" /> Student Groups
              </h3>
              <p className="text-[#57534E] text-sm mb-3">
                Create custom groups to organise your students however you need:
              </p>
              <ul className="text-sm text-[#57534E] space-y-1">
                {['High School Team', 'College 1st / 2nd Team', 'Beginner Ladies', 'Junior Development Squad', 'Tournament Players', 'Any custom name you choose'].map((g, i) => (
                  <li key={i} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />{g}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
              <h3 className="font-semibold text-[#1A1A1A] text-lg mb-3 flex items-center gap-2">
                <GitCompareArrows className="w-5 h-5 text-[#2C5530]" /> Compare Players
              </h3>
              <p className="text-[#57534E] text-sm mb-3">
                Select individual students or entire groups to compare across:
              </p>
              <ul className="text-sm text-[#57534E] space-y-1">
                {['Scoring — Avg score, best round, mental score', 'Sessions — Total count, hours, mood trends', 'Pillars — Meditation, Movement, Nutrition, Recovery', 'Scorecards — Before/During/After averages', 'Radar overview — All areas in one chart', 'Download comparison data for records'].map((c, i) => (
                  <li key={i} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" />{c}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Session Types */}
        <section className="mb-12 print:mb-6">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Calendar className="w-6 h-6 text-[#2C5530]" /> Session Types & Logging
          </h2>
          <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-[#7C3AED]" /> Mental Coaching
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1 ml-6">
                  <li>Visualization, breathing, self-talk</li>
                  <li>Focus & concentration drills</li>
                  <li>Pre-shot routine work</li>
                  <li>Mood before & after tracking</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#2563EB]" /> Technical Coaching
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1 ml-6">
                  <li>Putting, short game, driving</li>
                  <li>Iron play, wedge work</li>
                  <li>Focus areas with quality ratings</li>
                  <li>Key learnings & reflections</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-[#1A1A1A] mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#059669]" /> On-Course Coaching
                </h4>
                <ul className="text-sm text-[#57534E] space-y-1 ml-6">
                  <li>Course management strategies</li>
                  <li>Decision making under pressure</li>
                  <li>Recovery shot scenarios</li>
                  <li>Tournament round logging</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="mb-12 print:mb-6">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Dumbbell className="w-6 h-6 text-[#2C5530]" /> Pillars of Performance
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Brain, title: 'Mindset', desc: 'Meditation, visualisation, breath work', color: 'purple' },
              { icon: Dumbbell, title: 'Movement', desc: 'S&C, yoga, mobility, cardio', color: 'blue' },
              { icon: Apple, title: 'Nutrition', desc: 'Daily meals, game day plans', color: 'green' },
              { icon: Moon, title: 'Recovery', desc: 'Sleep, ice bath, massage, rest', color: 'red' },
            ].map((p, i) => (
              <div key={i} className={`p-4 bg-${p.color}-50 rounded text-center`}>
                <p.icon className={`w-8 h-8 text-${p.color}-600 mx-auto mb-2`} />
                <p className={`font-medium text-${p.color}-900`}>{p.title}</p>
                <p className={`text-xs text-${p.color}-700`}>{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Business Benefits */}
        <section className="mb-12 print:mb-6 print:break-before-page">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-[#2C5530]" /> Business Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#2C5530] text-white p-6 rounded">
              <h3 className="font-semibold text-xl mb-4 flex items-center gap-3"><UserPlus className="w-6 h-6" /> Bulk Subscription Discounts</h3>
              <p className="text-white/80 mb-4">Purchase Pro subscriptions for your students at 25% off. Minimum 6 subscriptions required.</p>
              <div className="bg-white/10 p-4 rounded">
                <p className="text-sm">Regular Yearly: <span className="line-through">$99</span></p>
                <p className="text-lg font-bold">Coach Price: $74.25/student/year</p>
              </div>
            </div>
            <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
              <h3 className="font-semibold text-[#1A1A1A] text-xl mb-4 flex items-center gap-3"><Share2 className="w-6 h-6 text-[#2C5530]" /> Referral Code System</h3>
              <p className="text-[#57534E] mb-4">Get a unique referral code that gives your students 25% off. Students are automatically linked to your dashboard.</p>
            </div>
            <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
              <h3 className="font-semibold text-[#1A1A1A] text-xl mb-4 flex items-center gap-3"><Mail className="w-6 h-6 text-[#2C5530]" /> Invitation System</h3>
              <p className="text-[#57534E]">Send email invitations to students. They receive a personalised link and are automatically connected to you as their coach.</p>
            </div>
            <div className="bg-white border border-[#E7E5E4] p-6 print:border-gray-300">
              <h3 className="font-semibold text-[#1A1A1A] text-xl mb-4 flex items-center gap-3"><LineChart className="w-6 h-6 text-[#2C5530]" /> Weekly Digest Reports</h3>
              <p className="text-[#57534E]">Receive automatic weekly email summaries of all student activity. Choose your preferred day and never miss important updates.</p>
            </div>
          </div>
        </section>

        {/* Retention */}
        <section className="mb-12 print:mb-6">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Heart className="w-6 h-6 text-[#2C5530]" /> Improve Golfer Retention
          </h2>
          <div className="bg-white border border-[#E7E5E4] p-8 print:border-gray-300">
            <div className="space-y-5">
              {[
                { title: 'Accountability Through Visibility', desc: 'When students know their coach can see their practice logs, they follow through on commitments.' },
                { title: 'Demonstrate Measurable Progress', desc: 'Show improvement over time with data-backed charts. Students see the value of continued coaching.' },
                { title: 'Personalised Feedback', desc: 'Review session logs before coaching calls to provide specific, relevant feedback every time.' },
                { title: 'Holistic Performance View', desc: 'Monitor all four pillars to identify what\'s really holding a student back.' },
                { title: 'Group Insights', desc: 'Compare team members to identify who\'s putting in the work and who needs extra motivation.' },
              ].map((r, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F2F0E9] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-[#2C5530]">{i + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A1A] mb-1">{r.title}</h4>
                    <p className="text-[#57534E]">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Getting Started */}
        <section className="mb-12 print:mb-6">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6 flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#2C5530]" /> Getting Started
          </h2>
          <div className="bg-[#F2F0E9] p-8 rounded-lg">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Request Access', desc: 'Submit your details via the Coach Access Request form. We\'ll review and set up your account.' },
                { step: '2', title: 'Invite Students', desc: 'Add students directly, use bulk subscriptions, or share your referral code for discounted rates.' },
                { step: '3', title: 'Start Coaching', desc: 'Monitor activity, generate reports, and compare players — all from one dashboard.' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 bg-[#2C5530] text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold">{s.step}</span>
                  </div>
                  <h4 className="font-semibold text-[#1A1A1A] mb-2">{s.title}</h4>
                  <p className="text-sm text-[#57534E]">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 print:hidden">
          <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-4">Ready to Elevate Your Coaching?</h2>
          <p className="text-[#57534E] mb-8 max-w-xl mx-auto">
            Join the Quiet Mind Golf coach community and give your students the tools they need to master their mental game.
          </p>
          <Link to="/coach-login" className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-8 py-4 font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors" data-testid="request-access-cta">
            Request Coach Access <ChevronRight className="w-5 h-5" />
          </Link>
        </section>

        {/* Print Footer */}
        <div className="hidden print:block mt-12 pt-6 border-t border-gray-300 text-center text-sm text-gray-500">
          <p>Quiet Mind Golf - Coach Portal Benefits Report</p>
          <p>&copy; {new Date().getFullYear()} Quiet Mind Golf. All rights reserved.</p>
          <p className="mt-2">www.quietmindgolf.co.uk</p>
        </div>
      </main>

      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print\\:break-before-page { break-before: page; }
        }
      `}</style>
    </div>
  );
};

export default CoachBenefitsPage;
